import { Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMsalIdToken } from '../../hooks/useMsalIdToken';
import { useState, SyntheticEvent, useEffect, useMemo } from 'react';
import { StudentForm } from './components/student';
import { ParentForm } from './components/parent';
import {
  ParentMyceetyaDetailType,
  StudentMyceetyaDetailType,
} from '../../types/myceetya.type';
import { useMyceetya, useMyceetyaMutation } from './useMyceetya';
import { SummaryView } from './components/summary-view';
import { Dialog } from '../../components/dialog';
import { languages } from './components/config';
import { ToastView } from '../../components/toast';

interface MyceetyaProps {
  isShown: boolean;
}

export const Myceetya = ({ isShown }: MyceetyaProps) => {
  const token = useMsalIdToken();
  const { currentMyceetya, isFetching, isLoading } = useMyceetya();

  const [isMyceetyaActive, setMyceetyaActive] = useState(false);
  const [currentView, setView] = useState<'default' | 'summary'>('default');
  const [tab, setTab] = useState(0);
  const [isValid, setValid] = useState(false);
  const [isSubmit, setSubmit] = useState(false);

  const createMyceetya = useMyceetyaMutation();

  const [myceetyaStudentDetails, setStudentValues] = useState<
    Array<StudentMyceetyaDetailType>
  >([]);

  const [myceetyaParentDetails, setParentValues] =
    useState<ParentMyceetyaDetailType | null>(null);

  const languageList = useMemo(() => {
    const list = [
      ...languages.concat(
        currentMyceetya?.myceetyaStudentDetails?.map(
          (item: { otherLanguageAtHome: string }) => item.otherLanguageAtHome,
        ),
      ),
    ];

    return [...new Set(list)];
  }, [currentMyceetya]);

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleSubmit = () => {
    const payload = {
      created: new Date().toISOString(),
      myceetyaStudentDetails: myceetyaStudentDetails.map((student) => {
        return {
          isAboriginalTorresStraitIslanderOrigin:
            student?.isAboriginalTorresStraitIslanderOrigin?.toString(),
          otherLanguageAtHome:
            student?.otherLanguageAtHome === 'Other'
              ? student?.otherLanguage
              : student?.otherLanguageAtHome,
          studentId: student?.studentId,
        };
      }),
      myceetyaParentDetail: {
        otherLanguageAtHome:
          myceetyaParentDetails?.otherLanguageAtHome === 'Other'
            ? myceetyaParentDetails?.otherLanguage
            : myceetyaParentDetails?.otherLanguageAtHome,
        highestYearCompleted: myceetyaParentDetails?.highestYearCompleted,
        highestQualificationCompleted:
          myceetyaParentDetails?.highestQualificationCompleted,
        ocupationGroup: myceetyaParentDetails?.ocupationGroup,
      },
    };

    void createMyceetya.mutate(payload);
  };

  const validationCheckAndSubmit = () => {
    /**
     * Validate student and parent details
     * check for any null values
     */
    if (myceetyaStudentDetails && myceetyaParentDetails) {
      
      const studentCheck =
        myceetyaStudentDetails.every(student =>
          student.firstname !== null &&
          student.surname !== null &&
          student.studentId !== null &&
          student.dateOfBirth !== null &&
          student.isAboriginalTorresStraitIslanderOrigin !== null && student.isAboriginalTorresStraitIslanderOrigin.length > 0 &&
          student.otherLanguageAtHome !== null &&
          student.address !== null &&
          student.otherLanguage !== null
        );

      const parentCheck =
        myceetyaParentDetails?.highestQualificationCompleted !== null &&
        myceetyaParentDetails?.highestYearCompleted !== null &&
        myceetyaParentDetails?.ocupationGroup !== null &&
        myceetyaParentDetails?.otherLanguageAtHome !== null;

      setValid(parentCheck && studentCheck);
      
    /**
     * summary view and submit data
     */
      currentView === 'default' ? parentCheck && studentCheck && setView('summary') : !createMyceetya?.isPending && handleSubmit()
    }
  };

  const TabView = () => {
    return (
      <Stack
        position='sticky'
        top='0'
        bgcolor='#FFFFFF'
        zIndex={1}
        width='100%'
      >
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label='Student' />
          <Tab label='Parent' />
        </Tabs>
      </Stack>
    );
  };

  useEffect(() => {
    if (currentMyceetya) {
      setStudentValues(currentMyceetya?.myceetyaStudentDetails);
      setParentValues(currentMyceetya?.myceetyaParentDetail);
    }
  }, [currentMyceetya]);

  useEffect(() => {
    if (createMyceetya?.isSuccess) {
      setMyceetyaActive(false);
    }
  }, [createMyceetya?.isSuccess]);

  useEffect(() => {
    if (isShown) {
      setMyceetyaActive(isShown);
    }
  }, [isShown]);

  return (
    <>
      <Dialog
        open={isMyceetyaActive}
        handleClose={() => {}}
        title='Data Collection'
        isLoading={isFetching || isLoading}
        isCloseHide={true}
        disableEscapeKeyDown={true}
        actions={
          <Stack
            flexDirection='row'
            justifyContent={tab === 0 ? 'flex-end' : 'space-between'}
            mx={1}
            py={2}
            width='100%'
          >
            {!(tab === 0) &&
              <Button
                onClick={() =>
                  currentView === 'default'
                    ? setTab(0)
                    : setView('default')
                }
                variant='contained'
                color='inherit'
                disabled={createMyceetya?.isPending}
              >
                {'Back'}
              </Button>
            }
            <Button
              onClick={() => {
                if(tab === 0){
                  setTab(1)
                } else {
                  setSubmit(true)
                  validationCheckAndSubmit()
                }
              }}
              variant='contained'
              disabled={createMyceetya?.isPending}
            >
              {currentView === 'default'
                ? ( tab === 0 ? 'Next' : 'Submit')
                : createMyceetya?.isPending
                ? 'Submitting...'
                : 'Submit'}
            </Button>
          </Stack>
        }
      >
        <Stack width='100%'>
          <>
            {currentView === 'default' ? (
              <Stack>
                <Stack alignItems='center' justifyContent='center'>
                  <TabView />
                  <Stack width='100%'>
                    <Stack display={tab === 0 ? 'flex' : 'none'}>
                      <StudentForm
                        currentDetail={currentMyceetya?.myceetyaStudentDetails}
                        myceetyaStudentDetails={myceetyaStudentDetails}
                        setStudentValues={setStudentValues}
                        languages={languageList}
                      />
                    </Stack>
                    <Stack display={tab === 1 ? 'flex' : 'none'}>
                      {myceetyaParentDetails && (
                        <ParentForm
                          currentDetail={currentMyceetya?.myceetyaParentDetail}
                          myceetyaParentDetails={myceetyaParentDetails}
                          setParentValues={setParentValues}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            ) : (
              <Stack>
                <SummaryView
                  details={{
                    students: myceetyaStudentDetails,
                    parent: myceetyaParentDetails,
                  }}
                />
              </Stack>
            )}
          </>
        </Stack>
        {isSubmit && currentView === 'default' && !isValid &&
          <Stack width='100%'>
            <Typography 
              fontSize={14}
              fontWeight={700}
              color='#FF0000'
            >
              {'Please ensure you have provided all data for your student(s) and yourself'}
            </Typography>
          </Stack>
        }
      </Dialog>
      <ToastView
        isOpen={createMyceetya.isSuccess}
        type='success'
        message='Successfully saved.'
      />
    </>
  );
};
